import { API_BASE } from "@/env.mapper";
import { getAuthStorageKeys } from "./auth-storage";
import { notifyAuthSessionChanged } from "./auth-session";
import { readAuthenticatedSession, sessionKey, clearAuthenticatedSessions, validateAuthenticatedSession } from "./authenticated-session";
import { checkSaleRequest, canAccessGroupSummary, saleAuthorizationMessage } from "./sale-permissions";
import axios, {
  AxiosInstance,
  AxiosError,
  InternalAxiosRequestConfig,
  AxiosResponse,
} from "axios";

const axiosInstance: AxiosInstance = axios.create({
  baseURL: API_BASE,
  timeout: 100000,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

let isRefreshing = false;

const authScope = () => {
  const access = getAuthStorageKeys().access;
  return `${localStorage.getItem(access) || ""}:${localStorage.getItem(sessionKey(access)) || ""}`;
};
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

const processQueue = (
  error: AxiosError | null,
  token: string | null = null,
) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

const refreshRequests = new Map<string, Promise<string>>();

export function refreshAccessToken(requirePermissions = false): Promise<string> {
  const key = `${authScope()}:${requirePermissions}`;
  const pending = refreshRequests.get(key);
  if (pending) return pending;
  const request = performTokenRefresh(requirePermissions).finally(() => {
    if (refreshRequests.get(key) === request) refreshRequests.delete(key);
  });
  refreshRequests.set(key, request);
  return request;
}

const performTokenRefresh = async (requirePermissions: boolean): Promise<string> => {
  const refreshToken = localStorage.getItem(getAuthStorageKeys().refresh);

  if (!refreshToken) {
    throw new Error("No refresh token available");
  }

  const previousScope = authScope();
  try {
    const accessKey = getAuthStorageKeys().access;
    const session = readAuthenticatedSession(accessKey);
    const response = await axios.post(`${API_BASE}/api/auth/refresh-token`, {
      refreshToken,
    });
    const data = response.data.data;
    const token = data.accessToken || data.token;
    const newRefreshToken = data.refreshToken;
    if (requirePermissions && (!Array.isArray(data.permissions) || !data.permissions.every((code: unknown) => typeof code === "string"))) {
      throw new Error("The authentication API did not return permissions. Access remains denied.");
    }
    if (authScope() !== previousScope) throw new axios.CanceledError("Authentication session changed");
    localStorage.setItem(getAuthStorageKeys().access, token);
    if (newRefreshToken) {
      localStorage.setItem(getAuthStorageKeys().refresh, newRefreshToken);
    }
    if (session) localStorage.setItem(sessionKey(accessKey), JSON.stringify(validateAuthenticatedSession({
      ...session, accessToken: token, refreshToken: newRefreshToken || session.refreshToken,
      user: data.user || session.user, permissions: data.permissions, sidebar: data.sidebar ?? session.sidebar,
    })));
    notifyAuthSessionChanged();

    return token;
  } catch (error) {
    if (axios.isCancel(error)) throw error;
    if (authScope() !== previousScope) throw new axios.CanceledError("Authentication session changed");
    localStorage.removeItem(getAuthStorageKeys().access);
    notifyAuthSessionChanged();
    localStorage.removeItem(getAuthStorageKeys().refresh);
    throw error;
  }
};

axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const session = readAuthenticatedSession(getAuthStorageKeys().access);
    const summaryPage = typeof window !== "undefined" && canAccessGroupSummary(session, window.location.pathname);
    checkSaleRequest(config.method || "get", config.url || "", session, summaryPage, config.data);
    (config as InternalAxiosRequestConfig & { authScope?: string }).authScope = authScope();
    const token = localStorage.getItem(getAuthStorageKeys().access);
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  },
);

axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => {
    const scope = (response.config as InternalAxiosRequestConfig & { authScope?: string }).authScope;
    if (scope !== undefined && scope !== authScope()) throw new axios.CanceledError("Authentication session changed");
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
      authScope?: string;
    };
    if (originalRequest?.authScope !== undefined && originalRequest.authScope !== authScope()) return Promise.reject(new axios.CanceledError("Authentication session changed"));
    if (axios.isCancel(error)) return Promise.reject(error);
    if (!originalRequest) return Promise.reject(error);

    if (error.response?.status === 401) {
      error.message = saleAuthorizationMessage(error, "Please sign in again.");
      if (error.response.data && typeof error.response.data === "object") {
        (error.response.data as { message?: string }).message = error.message;
      }
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      if (originalRequest.url?.includes("/auth/")) {
        localStorage.removeItem(getAuthStorageKeys().access);
        notifyAuthSessionChanged();
        localStorage.removeItem(getAuthStorageKeys().refresh);
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return axiosInstance(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      isRefreshing = true;
      const groupSession = readAuthenticatedSession(getAuthStorageKeys().access)?.authorization?.kind === "group-summary";

      try {
        const newToken = await refreshAccessToken();
        processQueue(null, newToken);

        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
        }

        return axiosInstance(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError as AxiosError, null);
        if (axios.isCancel(refreshError)) return Promise.reject(refreshError);
        clearAuthenticatedSessions();
        localStorage.removeItem(getAuthStorageKeys().access);
        notifyAuthSessionChanged();
        localStorage.removeItem(getAuthStorageKeys().refresh);
        if (typeof window !== "undefined") window.location.href = groupSession
          ? `/${window.location.search || ""}${window.location.hash || ""}` : getAuthStorageKeys().loginPath;
        error.message = saleAuthorizationMessage(error, "Please sign in again.");
        return Promise.reject(error);
      } finally {
        isRefreshing = false;
      }
    }

    if (error.response?.status === 403) {
      error.message = saleAuthorizationMessage(error, "Permission denied.");
      if (error.response.data && typeof error.response.data === "object") {
        (error.response.data as { message?: string }).message = error.message;
      }
    }

    if (error.response?.status === 500) {
      console.error("Internal server error");
    }

    return Promise.reject(error);
  },
);

export default axiosInstance;
